import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { TableColumnsProps } from "@lib/types/General";
import { IntlShape } from "react-intl";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { LanguageLabel } from "@lib/components/MiniComponents/Others";
import { Tag } from "antd";

import {
  PROCESSED_STATUS_COLOR,
  STATUS_COLOR,
  CLIENT_MOOD_COLOR,
  MarketPlaceNoteValidation,
  NoteValidationResponseData,
} from "@lib/types/NoteValidation";
import { DATE_FORMATS } from "@lib/constants/date";

type Props<T> = TableColumnsProps<T> & {
  handleSelectItem: (item: T) => void;
  messages: IntlShape["messages"];
};

const getColumns = <T extends MarketPlaceNoteValidation>(
  props: Props<T>,
): ColumnsType<T> => {
  const { handleSelectItem } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      dontSort: true,
      filterType: undefined,
      width: "180px",
      render: (_, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
    },
    {
      title: "common.language",
      dataIndex: "language",
      key: "language",
      width: "120px",
      render: (d) => <LanguageLabel languageCode={d} />,
    },
    {
      title: "common.rawNote",
      dataIndex: "rawNote",
      key: "rawNote",
      width: "300px",
      filterType: "search",
      render: (text: string) => (
        <div
          style={{
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ),
    },

    {
      title: "common.processedStatus",
      dataIndex: "processedStatus",
      key: "processedStatus",
      width: "150px",
      filterType: "dropdown",
      render: (status: MarketPlaceNoteValidation["processedStatus"]) =>
        status ? (
          <Tag color={PROCESSED_STATUS_COLOR[status] ?? "default"}>
            {status}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "common.status",
      dataIndex: "status",
      key: "status",
      width: "120px",
      filterType: "dropdown",
      render: (status: MarketPlaceNoteValidation["status"]) =>
        status ? (
          <Tag color={STATUS_COLOR[status] ?? "default"}>{status}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "common.clientMood",
      dataIndex: "responseData",
      key: "clientMood",
      width: "130px",
      dontSort: true,
      filterType: undefined,
      render: (responseData: MarketPlaceNoteValidation["responseData"]) => {
        const mood = (responseData as NoteValidationResponseData)?.clientMood;
        return mood ? (
          <Tag color={CLIENT_MOOD_COLOR[mood] ?? "default"}>{mood}</Tag>
        ) : (
          "-"
        );
      },
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "150px",
      render: (d) =>
        d ? dayjs(d).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME) : "-",
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "150px",
      render: (d) =>
        d ? dayjs(d).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME) : "-",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      key: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelectItem(record)}
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
